/**
 * `BULK_MUTATE` step. Single-call wrapper over `POST /api/v1/core/route/stops/bulk`
 * (`bulkMoveStops`). Each batch's `updateMasterSequence` is set by the
 * caller from `shouldIncludeMaster(intent)`. Uses `skipInvalidate:true`.
 */
import { err, ok } from 'types/entities/shared/result'

import { extractBackendError } from '../backendError'

import type { StepResult } from '../types'
import type { BulkMoveStopsBody } from 'types/api/route/bulkRequests'
import type { Deps, PipelineStep } from 'types/modules/routing'

export interface BulkMutateStepArgs {
  readonly body: BulkMoveStopsBody
}

export type BulkMutateStepValue = { readonly committed: true }

export type BulkMutateStepResult = StepResult<'BULK_MUTATE', BulkMutateStepValue>

const STEP_ID = 'bulk-mutate'

export const makeBulkMutateStep = (args: BulkMutateStepArgs, deps: Deps): PipelineStep<'BULK_MUTATE', BulkMutateStepResult> => {
  return {
    id: STEP_ID,
    phase: 'BULK_MUTATE',
    label: 'Failed to commit bulk save',
    applyOrder: 200,
    run: async () => {
      if (!deps.bulkMutate) {
        return err({
          kind: 'PRECONDITION_FAILED',
          reason: 'bulkMutate dep not provided — the v1-bulk strategy requires deps.bulkMutate to be wired',
        })
      }
      try {
        await deps
          .bulkMutate({
            ...args.body,
            // Mutation arg shape extends BulkMoveStopsBody — `skipInvalidate`
            // is a non-body knob the api/route/moveStops.js layer reads
            // off the arg before posting.
            skipInvalidate: true,
          } as unknown as Parameters<NonNullable<Deps['bulkMutate']>>[0])
          .unwrap()
        return ok({ phase: 'BULK_MUTATE', stepId: STEP_ID, value: { committed: true } })
      } catch (cause: unknown) {
        const { status, message } = extractBackendError(cause)
        return err({
          kind: 'BE_REJECTED',
          phase: 'BULK_MUTATE',
          status,
          message,
        })
      }
    },
  }
}
